import { useReadQuery } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { CaretArrowDownIcon } from '@synoptic/ui-kit/icons/react/caret-arrow-down.js';
import { CaretArrowUpIcon } from '@synoptic/ui-kit/icons/react/caret-arrow-up.js';
import { UserIcon } from '@synoptic/ui-kit/icons/react/user.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { borderRadius } from '@synoptic/ui-kit/tokens.js';
import { clsx } from 'clsx';
import { useState } from 'react';
import { S3ObjectInput } from '@/__generated__/graphql.ts';
import { CoverImage } from '@/components/cover-image/cover-image.tsx';
import { LayoutCard } from '@/components/layout/card.tsx';
import { getUserS3Url } from '@/components/s3/user/get-user-s3-url.ts';
import { updateImageBackgroundImg } from '@/components/update-user-image/update-image-modal.css.ts';
import { UpdateImageModal } from '@/components/update-user-image/update-image-modal.tsx';
import { useUpdateBackgroundImage } from '@/components/update-user-image/use-update-background.ts';
import { useProfileLoaderData } from '@/routes/_app.$username/loader.ts';
import { emptyProfile } from '@/routes/_app.$username/page.css.ts';
import { useIsMe } from '../use-is-me.ts';
import { DetailedStatistics } from './detailed-statistics/detailed-statistics.tsx';
import { MainStatistics } from './main-statistics/main-statistics.tsx';
import {
  bodyContainer,
  coverImage,
  editableImage,
  footerContainer,
  profileCardContainer,
  showMoreButton,
} from './profile-card.css.ts';

export const ProfileCard = () => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const isMe = useIsMe();
  const isTablet = useIsTablet();

  const [updateBackgroundImage] = useUpdateBackgroundImage();
  const onBgImageUpdate = (image: S3ObjectInput) => {
    updateBackgroundImage({ backgroundImage: image });
  };

  const { profileQueryRef } = useProfileLoaderData();
  const {
    data: { user },
  } = useReadQuery(profileQueryRef);

  if (!user) {
    return (
      <EmptyState
        className={emptyProfile}
        illustration={<UserIcon />}
        heading={'User does not exist'}
      />
    );
  }

  const { id: userId, backgroundImage } = user;

  const url = getUserS3Url(userId, backgroundImage?.key);

  return (
    <LayoutCard className={profileCardContainer}>
      {isMe ? (
        <UpdateImageModal
          trigger={
            <div>
              <CoverImage
                s3Key={backgroundImage?.key}
                userId={userId}
                className={clsx(coverImage, editableImage)}
              />
            </div>
          }
          onUpdate={onBgImageUpdate}
          mainTextEmpty={'Pick a cover picture'}
          subTextEmpty={'Recommended size: 1500x500 pixels.'}
          mainText={'Looking good!'}
          subText={
            "You're almost set! Make sure your new background image looks great before finalizing it."
          }
          editorProps={{
            width: isTablet ? 600 : 300,
            height: isTablet ? 150 : 75,
            borderRadius: borderRadius.r16,
            cropperPadding: 15,
          }}
          renderPreview={(previewSrc) => (
            <CoverImage
              src={previewSrc || url?.toString()}
              className={updateImageBackgroundImg}
            />
          )}
          previewBorderRadius={borderRadius.r16}
        />
      ) : (
        <CoverImage
          s3Key={backgroundImage?.key}
          userId={userId}
          className={coverImage}
        />
      )}
      <div className={bodyContainer}>
        <MainStatistics userId={userId} />
        {showMore && <DetailedStatistics userId={userId} />}
      </div>
      <div className={footerContainer}>
        <Button
          variant="ghost"
          endIcon={!showMore ? <CaretArrowDownIcon /> : <CaretArrowUpIcon />}
          className={showMoreButton}
          onClick={() => setShowMore((prevState) => !prevState)}
        >
          {!showMore ? 'More statistics' : 'Hide'}
        </Button>
      </div>
    </LayoutCard>
  );
};
